import { useCallback, useEffect, useRef } from "react";

function prefersReducedMotion() {
  if (typeof window === "undefined") return true;
  return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
}

/**
 * Adds `is-visible` class to all elements with [data-reveal] when they enter viewport.
 * Lightweight alternative to animation libraries.
 */
export function useRevealOnScroll() {
  useEffect(() => {
    if (prefersReducedMotion()) {
      document
        .querySelectorAll("[data-reveal]")
        .forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const els = Array.from(document.querySelectorAll("[data-reveal]"));
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        }
      },
      { root: null, threshold: 0.12, rootMargin: "0px 0px -10% 0px" }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/**
 * Parallax hook: attach refs with different speeds.
 * Usage: const parallax = useParallax(); <div ref={parallax(0.08)} />
 */
export function useParallax() {
  const nodesRef = useRef([]);
  const rafRef = useRef(null);
  const lastYRef = useRef(0);

  useEffect(() => {
    if (prefersReducedMotion()) return;

    const update = () => {
      const y = window.scrollY || 0;
      if (Math.abs(y - lastYRef.current) < 2) {
        rafRef.current = null;
        return;
      }

      lastYRef.current = y;

      for (const item of nodesRef.current) {
        if (!item?.el) continue;
        const speed = item.speed ?? 0.08;
        // Move opposite to scroll slightly to create depth.
        const translate = Math.round(y * speed * -1);
        item.el.style.transform = `translate3d(0, ${translate}px, 0)`;
      }

      rafRef.current = null;
    };

    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const register = useCallback(
    (speed = 0.08) =>
      (el) => {
        if (!el) return;
        // Avoid duplicates
        nodesRef.current = nodesRef.current.filter((n) => n.el !== el);
        el.style.willChange = "transform";
        nodesRef.current.push({ el, speed });
      },
    []
  );

  return register;
}
